// Schemas for routes-crm.ts (browser-safe zod). Native HubSpot/HighLevel adapters (PRD §5, §6, §10, §14, §15).
import { z } from 'zod';
import { FunnelStageSchema, parseFunnelStage } from './api-funnel.js';
import { CapabilityReportSchema } from './api.js';
import { CrmAttributionMappingSchema } from './api-attribution.js';
import { WhatsAppTemplateSchema } from './api-whatsapp.js';
import { ConnectionStatusSchema, UnitAvailabilitySchema } from './enums.js';

const Id = z.string().min(1).max(64);
const Iso = z.string();
const Field = z.string().min(1).max(200);

export const CRM_PROVIDERS = ['hubspot', 'highlevel'] as const;
export const CrmProviderSchema = z.enum(CRM_PROVIDERS);
export type CrmProvider = z.infer<typeof CrmProviderSchema>;

export const CrmAuthMethodSchema = z.enum(['oauth', 'private_token']);
export type CrmAuthMethod = z.infer<typeof CrmAuthMethodSchema>;

// ───────────── Mapping (stored in IntegrationConnection.settings.mapping) ─────────────

/**
 * Where the development (project) is read from on the CRM record:
 *  - property:  a contact/deal property (HubSpot) or contact field / custom field id (HighLevel)
 *  - pipeline:  the deal pipeline (HubSpot) / opportunity pipeline id (HighLevel)
 */
export const CrmProjectMappingSchema = z.object({
  source: z.enum(['property', 'pipeline']).default('property'),
  /** Property name / custom field id holding the development value (ignored for `pipeline`). */
  field: z.string().max(200).nullable().default(null),
  values: z.array(z.object({ value: z.string().min(1).max(200), projectId: Id })).max(200).default([]),
  /** Used only when the record carries no mapped value. null = quarantine unmapped records (never guessed). */
  defaultProjectId: Id.nullable().default(null),
});

export const CrmOwnerMappingSchema = z.object({ externalOwnerId: z.string().min(1).max(100), membershipId: Id });

export const CrmQualificationMappingSchema = z.object({
  /** Our qualification key (LeadInterest.qualification), e.g. budget, timeframe, financing. */
  field: z.string().min(1).max(80),
  /** CRM property name (HubSpot) or custom field id (HighLevel). */
  property: Field,
});

/**
 * CRM-only outbound marker (plan 1C): the stage/owner written when a human takes the opportunity («Atención humana»).
 * It is NOT a funnel stage — the funnel keeps its fixed meanings (§15.5); it only exists in the outbound mapping.
 */
export const CRM_HUMAN_ATTENTION_MARKER = 'human_attention' as const;

/** Legacy stage spellings are normalized; entries whose stage is not a fixed LIA stage are dropped (never guessed). */
const stageEntries = <T extends z.ZodType>(entry: T, extra: readonly string[] = []) =>
  z.preprocess(
    (v) =>
      Array.isArray(v)
        ? v
            .map((e) =>
              e && typeof e === 'object' && typeof (e as { stage?: unknown }).stage === 'string'
                ? { ...e, stage: extra.includes((e as { stage: string }).stage) ? (e as { stage: string }).stage : parseFunnelStage((e as { stage: string }).stage) }
                : e,
            )
            .filter((e) => !(e && typeof e === 'object' && (e as { stage?: unknown }).stage === null))
        : v,
    z.array(entry).max(100).default([]),
  );

export const CrmStageMappingSchema = z.object({
  /** Contact property (HubSpot, e.g. hs_lead_status) or `pipelineStageId` (HighLevel opportunities). */
  field: z.string().max(200).nullable().default(null),
  /** CRM value → fixed LIA stage. The stage MEANING never changes per CRM (§15.5); only this mapping is config. */
  inbound: stageEntries(z.object({ externalValue: z.string().min(1).max(200), stage: FunnelStageSchema })),
  /** Fixed LIA stage → CRM pipeline stage / property value written back (only when writeEnabled). */
  outbound: stageEntries(z.object({ stage: z.union([FunnelStageSchema, z.literal(CRM_HUMAN_ATTENTION_MARKER)]), externalValue: z.string().min(1).max(200) }), [CRM_HUMAN_ATTENTION_MARKER]),
  /** The CRM is the stage authority by default: we only write stages when explicitly enabled. */
  writeEnabled: z.boolean().default(false),
});

/** Inventory/apartado mapping for CRM-authoritative projects (PRD §10): the client's actual record type and fields. */
export const CrmInventoryMappingSchema = z.object({
  enabled: z.boolean().default(false),
  /** HubSpot object type (deals, p_apartados, 2-123456…) / HighLevel: opportunity | contact. */
  objectType: z.string().max(100).default('deals'),
  unitField: z.string().max(200).default(''),
  statusField: z.string().max(200).default(''),
  /** Monotonic revision field (number or ISO date). Default: the record's last-modified timestamp. */
  revisionField: z.string().max(200).nullable().default(null),
  reservationIdField: z.string().max(200).nullable().default(null),
  holdExpiresField: z.string().max(200).nullable().default(null),
  statusValues: z.array(z.object({ externalValue: z.string().min(1).max(200), availability: UnitAvailabilitySchema })).max(100).default([]),
});

export const CrmContactFieldsSchema = z.object({
  name: z.string().max(200).nullable().default(null),
  phone: z.string().max(200).nullable().default(null),
  email: z.string().max(200).nullable().default(null),
});

export const CrmWritebackSettingsSchema = z.object({
  enabled: z.boolean().default(true),
  /** One writer per activity type: turn off when Aircall's native CRM integration already logs calls. */
  logCalls: z.boolean().default(true),
  logAppointments: z.boolean().default(true),
  updateQualification: z.boolean().default(true),
  noteOnHandoff: z.boolean().default(true),
  /** Assign the CRM owner from our advisor (only when the owner mapping allows it; CRM is owner authority by default). */
  assignOwner: z.boolean().default(false),
  /** Setter §08.2: one note per meaningful WhatsApp exchange (debounced conversation summary, never every message). */
  logConversations: z.boolean().default(true),
});

/**
 * Setter §08.1 (CRM side): one deal (HubSpot) / opportunity (HighLevel) per LIA opportunity (contact + project).
 * Create-or-find with dedupe; only projects with a pipeline here get a deal (never guessed).
 */
export const CrmDealPipelineSchema = z.object({
  projectId: Id,
  /** HubSpot deal pipeline id / HighLevel opportunity pipeline id. */
  pipelineId: z.string().min(1).max(200),
  /** Stage the deal is created in (HubSpot dealstage id / HighLevel pipelineStageId). */
  initialStageId: z.string().min(1).max(200),
});
export const CrmDealMappingSchema = z.object({
  enabled: z.boolean().default(false),
  pipelines: z.array(CrmDealPipelineSchema).max(200).default([]),
  /** Optional deal property (HubSpot) / custom field id (HighLevel) that stores the project name (dedupe + reports). */
  projectProperty: z.string().max(200).nullable().default(null),
});
export type CrmDealMapping = z.infer<typeof CrmDealMappingSchema>;

/** Setter §08.2: «siguiente acción» written to the CRM (label + date) — only fields the team mapped. */
export const CrmNextActionMappingSchema = z.object({
  property: z.string().max(200).nullable().default(null),
  dateProperty: z.string().max(200).nullable().default(null),
  /** Record that carries the fields: the contact, or the opportunity's deal when one is linked. */
  target: z.enum(['contact', 'deal']).default('contact'),
});

/** Setter §09.9: contact property that receives the preferred language (es/en) and the value written for each. */
export const CrmLanguageMappingSchema = z.object({
  property: z.string().max(200).nullable().default(null),
  values: z.object({ es: z.string().min(1).max(100).default('es'), en: z.string().min(1).max(100).default('en') }).default({ es: 'es', en: 'en' }),
});

/** Setter §09.4: optional contact property that receives the opportunity's objections (category + status, short text). */
export const CrmObjectionsMappingSchema = z.object({
  property: z.string().max(200).nullable().default(null),
});

/** Setter §08.2: CRM tasks for human follow-ups (handoff, pending appointment outcome, reviews/incidents). */
export const CrmTaskMappingSchema = z.object({
  enabled: z.boolean().default(false),
  handoff: z.boolean().default(true),
  pendingOutcome: z.boolean().default(true),
  /** Reviews: a manual CRM «Cita agendada» without a booking, incidents. */
  review: z.boolean().default(true),
  /** v1: LIA's own task at every stage (one open task on the deal, owned by «LIA actúa como»), closed with its result. */
  setter: z.boolean().default(true),
  /** CRM owner for tasks whose responsible person has no owner mapping (null = the task stays unassigned). */
  defaultOwnerExternalId: z.string().max(100).nullable().default(null),
});

/** Setter §08.7: live CRM re-read before every automatic external action (stage/owner freshness window). */
export const CrmRevalidationSchema = z.object({
  enabled: z.boolean().default(true),
  freshnessMinutes: z.number().int().min(1).max(1440).default(10),
});

/**
 * LIA as a member of the CRM team (v1 «LIA actúa como»): the CRM owner (HubSpot owner / HighLevel user) that stands for
 * LIA in the CRM's own distribution. When set, LIA starts work only on NEW records whose owner is this one and that were
 * created after the project's activation; reassigning older records to it does not start contact (v1.1 §02 entry rule).
 */
export const CrmAgentSchema = z.object({
  ownerExternalId: z.string().max(100).nullable().default(null),
});

export const CrmMappingSchema = z.object({
  projects: CrmProjectMappingSchema.default({ source: 'property', field: null, values: [], defaultProjectId: null }),
  owners: z.array(CrmOwnerMappingSchema).max(200).default([]),
  qualification: z.array(CrmQualificationMappingSchema).max(50).default([]),
  stages: CrmStageMappingSchema.default({ field: null, inbound: [], outbound: [], writeEnabled: false }),
  inventory: CrmInventoryMappingSchema.default({
    enabled: false,
    objectType: 'deals',
    unitField: '',
    statusField: '',
    revisionField: null,
    reservationIdField: null,
    holdExpiresField: null,
    statusValues: [],
  }),
  contactFields: CrmContactFieldsSchema.default({ name: null, phone: null, email: null }),
  writeback: CrmWritebackSettingsSchema.default({ enabled: true, logCalls: true, logAppointments: true, updateQualification: true, noteOnHandoff: true, assignOwner: false, logConversations: true }),
  /** Source/campaign fields read from and (optionally) written to the CRM (spec §08.6). */
  attribution: CrmAttributionMappingSchema.default({ inbound: [], outbound: [], writeEnabled: false }),
  deals: CrmDealMappingSchema.default({ enabled: false, pipelines: [], projectProperty: null }),
  nextAction: CrmNextActionMappingSchema.default({ property: null, dateProperty: null, target: 'contact' }),
  tasks: CrmTaskMappingSchema.default({ enabled: false, handoff: true, pendingOutcome: true, review: true, setter: true, defaultOwnerExternalId: null }),
  revalidation: CrmRevalidationSchema.default({ enabled: true, freshnessMinutes: 10 }),
  language: CrmLanguageMappingSchema.default({ property: null, values: { es: 'es', en: 'en' } }),
  objections: CrmObjectionsMappingSchema.default({ property: null }),
  agent: CrmAgentSchema.default({ ownerExternalId: null }),
  /** Owner decision C2: this CRM's meeting links (HubSpot) are offered as a calendar (project «Agenda»). Off by default. */
  calendar: z.object({ enabled: z.boolean().default(false) }).default({ enabled: false }),
});
export type CrmMapping = z.infer<typeof CrmMappingSchema>;
export type CrmMappingInput = z.input<typeof CrmMappingSchema>;

// ───────────── Connection ─────────────

export const CrmConnectionSchema = z.object({
  id: Id,
  provider: CrmProviderSchema,
  label: z.string(),
  status: ConnectionStatusSchema,
  method: CrmAuthMethodSchema,
  /** HubSpot portal (hub) id or HighLevel location id. */
  externalAccountId: z.string().nullable(),
  externalAccountName: z.string().nullable(),
  scopes: z.array(z.string()),
  capabilities: CapabilityReportSchema,
  capabilitiesCheckedAt: Iso.nullable(),
  healthMessage: z.string().nullable(),
  lastHealthAt: Iso.nullable(),
  tokenExpiresAt: Iso.nullable(),
  /** Provider-native webhook URL for this connection (signature or connection token). */
  webhookUrl: z.string(),
  /** App-level webhook URL (OAuth apps: one target URL for every installed portal/location, routed by account id). */
  appWebhookUrl: z.string().nullable(),
  hasInboundToken: z.boolean(),
  hasSigningSecret: z.boolean(),
  mapping: CrmMappingSchema,
  openSyncFailures: z.number().int(),
  createdAt: Iso,
});
export type CrmConnection = z.infer<typeof CrmConnectionSchema>;

export const StartCrmOauthSchema = z.object({ provider: CrmProviderSchema, label: z.string().min(1).max(120).optional() });
export const StartCrmOauthResultSchema = z.object({ authorizeUrl: z.string(), expiresAt: Iso });

export const CrmOauthCallbackQuerySchema = z.object({
  code: z.string().max(2000).optional(),
  state: z.string().max(200).optional(),
  error: z.string().max(200).optional(),
  error_description: z.string().max(1000).optional(),
  /** HighLevel may append these; ignored for trust decisions. */
  locationId: z.string().max(100).optional(),
});

export const CreateCrmTokenConnectionSchema = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal('hubspot'),
    label: z.string().min(1).max(120),
    /** HubSpot private app access token (pat-…). */
    accessToken: z.string().min(10).max(4000),
    /** Private app client secret: only needed to verify X-HubSpot-Signature-v3 on the private app's webhooks. */
    clientSecret: z.string().min(8).max(400).optional(),
  }),
  z.object({
    provider: z.literal('highlevel'),
    label: z.string().min(1).max(120),
    /** HighLevel Private Integration Token (sub-account scope). */
    accessToken: z.string().min(10).max(4000),
    locationId: z.string().min(3).max(100),
  }),
]);
export type CreateCrmTokenConnection = z.infer<typeof CreateCrmTokenConnectionSchema>;

/** Connect result: the inbound connection token (for CRM workflow webhooks that cannot sign) is shown exactly once. */
export const CrmConnectResultSchema = z.object({ connection: CrmConnectionSchema, inboundToken: z.string() });

// ───────────── Samples, preview, dry run ─────────────

export const CrmSampleSchema = z.object({
  id: Id,
  eventId: z.string(),
  type: z.string(),
  status: z.string(),
  statusDetail: z.string().nullable(),
  receivedAt: Iso,
  payload: z.record(z.string(), z.unknown()),
});

export const CrmPreviewRequestSchema = z.object({
  /** A captured or pasted provider payload (HubSpot webhook array/object, HighLevel webhook, or a CRM record). */
  payload: z.unknown(),
  /** Try the mapping being edited without saving it. */
  mapping: CrmMappingSchema.optional(),
});

export const CrmPreviewItemSchema = z.object({
  eventId: z.string().nullable(),
  sourceType: z.string(),
  kind: z.enum(['lead', 'inventory', 'ignored', 'invalid']),
  normalized: z.record(z.string(), z.unknown()).nullable(),
  actions: z.array(z.object({ step: z.string(), detail: z.string().nullable(), ok: z.boolean() })),
  needsFetch: z.boolean(),
});
export const CrmPreviewResultSchema = z.object({ items: z.array(CrmPreviewItemSchema), errors: z.array(z.string()) });
export type CrmPreviewItem = z.infer<typeof CrmPreviewItemSchema>;

// ───────────── Sync failures ─────────────

export const SyncFailureSchema = z.object({
  id: Id,
  connectionId: z.string().nullable(),
  operation: z.string(),
  objectType: z.string().nullable(),
  objectId: z.string().nullable(),
  error: z.string(),
  retryable: z.boolean(),
  resolvedAt: Iso.nullable(),
  createdAt: Iso,
});
export type SyncFailure = z.infer<typeof SyncFailureSchema>;
export const SyncFailureListQuerySchema = z.object({ status: z.enum(['open', 'resolved', 'all']).default('open'), connectionId: Id.optional() });

export const CrmHealthSchema = z.object({ connection: CrmConnectionSchema, checks: z.array(z.object({ key: z.string(), ok: z.boolean(), detail: z.string().nullable() })) });

export const CrmWebhookAckSchema = z.object({ received: z.number().int(), duplicates: z.number().int(), ignored: z.number().int() });
export const CrmOauthCallbackResponseSchema = z.object({}).passthrough();

// ───────── WhatsApp through the number connected in HighLevel (docs/plans/crm-whatsapp-plan.md §1) ─────────

const E164 = z.string().regex(/^\+[1-9]\d{7,14}$/, 'Número en formato internacional, por ejemplo +525512345678');

export const HighLevelWhatsAppTemplateSchema = WhatsAppTemplateSchema.extend({ providerTemplateId: z.string() });
export type HighLevelWhatsAppTemplate = z.infer<typeof HighLevelWhatsAppTemplateSchema>;

export const HighLevelWhatsAppStateSchema = z.object({
  /** false: this connection cannot carry WhatsApp (private token, or the app is not configured) — see reason. */
  available: z.boolean(),
  reason: z.string().nullable(),
  requiredScopes: z.array(z.string()),
  missingScopes: z.array(z.string()),
  enabled: z.boolean(),
  endpointId: z.string().nullable(),
  phoneE164: z.string().nullable(),
  displayName: z.string().nullable(),
  /** send / receive / coexistence_echo (rep replies from HighLevel), each verified only by real evidence. */
  capabilities: CapabilityReportSchema,
  templates: z.array(HighLevelWhatsAppTemplateSchema),
  test: z
    .object({
      to: z.string(),
      sentAt: Iso,
      providerMessageId: z.string().nullable(),
      /** accepted = HighLevel took it (not proof of delivery); confirmed = an admin saw it arrive. */
      status: z.enum(['accepted', 'confirmed', 'failed', 'unknown']),
      failureReason: z.string().nullable(),
    })
    .nullable(),
  /** Unattended sends (AI, routines) also need a rep reply from HighLevel to have been seen (the AI must pause on it). */
  automationReady: z.boolean(),
  automationReason: z.string().nullable(),
});
export type HighLevelWhatsAppState = z.infer<typeof HighLevelWhatsAppStateSchema>;

export const EnableHighLevelWhatsAppSchema = z.object({ phoneE164: E164, displayName: z.string().trim().min(1).max(80).optional() });
export const SaveHighLevelTemplatesSchema = z.object({
  templates: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(512),
        language: z.string().trim().min(2).max(15),
        providerTemplateId: z.string().trim().min(1).max(120),
        bodyText: z.string().trim().min(1).max(1024),
      }),
    )
    .max(100),
});
export const HighLevelWhatsAppTestSchema = z.object({ to: E164, text: z.string().trim().min(1).max(1000) });

// ───────── HubSpot inbox bridge: WhatsApp (Kapso) mirrored into a HubSpot custom channel (plan §2) ─────────

export const HubspotInboxStateSchema = z.object({
  available: z.boolean(),
  reason: z.string().nullable(),
  requiredScopes: z.array(z.string()),
  missingScopes: z.array(z.string()),
  enabled: z.boolean(),
  inboxId: z.string().nullable(),
  inboxName: z.string().nullable(),
  /** The WhatsApp number (endpoint) whose conversations are mirrored. */
  endpointId: z.string().nullable(),
  phoneE164: z.string().nullable(),
  channelAccountId: z.string().nullable(),
  /** mirror = a message was published into HubSpot; rep_replies = a reply written in HubSpot was received. */
  capabilities: CapabilityReportSchema,
  lastError: z.string().nullable(),
  /** WhatsApp numbers of this workspace that can be bridged. */
  numbers: z.array(z.object({ endpointId: z.string(), displayName: z.string(), phoneE164: z.string().nullable() })),
});
export type HubspotInboxState = z.infer<typeof HubspotInboxStateSchema>;
export const EnableHubspotInboxSchema = z.object({ inboxId: z.string().min(1).max(64), endpointId: z.string().min(1).max(64) });
export const HubspotInboxListSchema = z.object({ items: z.array(z.object({ id: z.string(), name: z.string() })) });
